import {InjectionToken} from '@angular/core';

/**
 * Политика маршрутизации приложения — инжектируется приложением (SEO-8).
 *
 * Гарды этой библиотеки используются **обоими**
 * приложениями (`compob2b-customer` и `compob2b-admin`). Бэк проверяет доступ как
 * `strpos(:url, menu.uri) > 0`, то есть значения `menu.uri` в БД — контракт для
 * фронтовых путей. У админки они совпадают с её URL, у кабинета после переезда в
 * корень — уже нет.
 *
 * Зашить карту URL кабинета сюда нельзя: правила вида `/news` →
 * `/cabinet/news/common` молча переписывали бы и админские `/ch`-запросы. Это ровно
 * та ошибка, от которой ТЗ §11.1 уберёг `StoreService.getMenu()`. Поэтому политику
 * задаёт приложение, а дефолты сохраняют ровно текущее поведение.
 *
 * Дефолты вынесены в константы и подставляются В ДВУХ местах: фабрикой токена (для
 * настоящего приложения) и fallback'ом при `inject(..., {optional: true})` в сервисе.
 * Второе нужно потому, что фабрика `providedIn: 'root'` не срабатывает в голом
 * `Injector.create` — а спеки гарда строят именно такой инжектор.
 */

/**
 * Преобразование URL навигации в форму, которую понимает `menu.uri` на бэке.
 * Дефолт — тождество: админка и любой другой потребитель не затронуты.
 *
 * Кабинет провайдит `toLegacyUrl` (`legacy-url-map.ts`) — форму, которая
 * сохраняет query: гард нормализует `state.url`, а не голый путь.
 */
export const DEFAULT_MENU_ACCESS_URL_NORMALIZER = (url: string): string => url;

export const MENU_ACCESS_URL_NORMALIZER = new InjectionToken<(url: string) => string>(
  'MENU_ACCESS_URL_NORMALIZER',
  {providedIn: 'root', factory: () => DEFAULT_MENU_ACCESS_URL_NORMALIZER},
);

/**
 * Куда уводить анонима при отказе в доступе (форма входа).
 *
 * Дефолт `['/cabinet', 'sign-in']` — историческое поведение: у кабинета этот роут
 * существовал, а в админке (где его нет) отрабатывал её `**` → `/sign-in`.
 * После переезда кабинет провайдит `['/sign-in']`.
 */
export const DEFAULT_SIGN_IN_ROUTE: readonly string[] = ['/cabinet', 'sign-in'];

export const SIGN_IN_ROUTE = new InjectionToken<readonly string[]>(
  'SIGN_IN_ROUTE',
  {providedIn: 'root', factory: () => DEFAULT_SIGN_IN_ROUTE},
);

/**
 * Главная страница приложения — цель редиректа «тебе тут делать нечего»
 * (`GuestGuardService` уводит уже авторизованного с формы входа,
 * `AuthGuardCustomerService` — не-покупателя с клиентских страниц).
 *
 * Дефолт `/cabinet/dashboard` — путь админки, он же прежняя зашитая константа обоих
 * гардов. Кабинет после переезда провайдит `/`: своего `/cabinet/dashboard` у него
 * больше нет, и до появления токена эти навигации уходили в `**`, спасаясь слоем
 * совместимости (`legacyUrlGuard`) — то есть лишним хопом через 404-ветку роутера.
 */
export const DEFAULT_HOME_ROUTE = '/cabinet/dashboard';

export const HOME_ROUTE = new InjectionToken<string>(
  'HOME_ROUTE',
  {providedIn: 'root', factory: () => DEFAULT_HOME_ROUTE},
);
